import React, { useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import { Clock, TrendingUp, DollarSign, AlertCircle, Zap, Lightbulb } from 'lucide-react';
import { TimingKPIs, ChartTheme } from './types';
import { KPICard, KPICardGrid } from './KPICard';
import { getBaseChartOptions, formatCurrency } from './chartUtils';
import { Tooltip } from '@mui/material';

import { useTranslation } from 'react-i18next';

// Initialiser le module Heatmap une seule fois
if (typeof HeatmapModule === 'function') {
    HeatmapModule(Highcharts);
}

interface TimingSectionProps {
    data: TimingKPIs;
    theme: ChartTheme;
}

// Helper pour générer les jours traduits
const getDayLabels = (locale: string, format: 'short' | 'long') => {
    // 1er janvier 2024 était un lundi
    const baseDate = new Date('2024-01-01T12:00:00Z');
    const labels = [];
    for (let i = 0; i < 7; i++) {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + i);
        labels.push(date.toLocaleDateString(locale, { weekday: format }));
    }
    return labels;
};

// Labels des heures
const hourLabels = Array(24).fill(null).map((_, i) => `${i.toString().padStart(2, '0')}h`);

type HeatmapMetric = 'orders' | 'revenue' | 'avgCart';

export const TimingSection: React.FC<TimingSectionProps> = ({ data, theme }) => {
    const { t, i18n } = useTranslation();
    const [selectedMetric, setSelectedMetric] = useState<HeatmapMetric>('orders');
    const baseOptions = getBaseChartOptions(theme);

    const dayLabels = React.useMemo(() => getDayLabels(i18n.language, 'short'), [i18n.language]);
    const fullDayLabels = React.useMemo(() => getDayLabels(i18n.language, 'long'), [i18n.language]);

    // Données sécurisées
    const heatmapData = data?.heatmapData || [];
    const topSlots = data?.topSlots || [];
    const bestSlot = data?.bestSlot;

    // Préparer les données pour la heatmap
    const prepareHeatmapData = () => {
        const points: [number, number, number][] = [];
        let maxValue = 0;

        heatmapData.forEach((dayData, day) => {
            dayData.forEach((slotData, hour) => {
                const value = selectedMetric === 'orders'
                    ? slotData.orders
                    : selectedMetric === 'revenue'
                        ? slotData.revenue
                        : slotData.avgCart;

                if (value > maxValue) maxValue = value;
                points.push([hour, day, value]);
            });
        });

        return { points, maxValue };
    };

    const { points: heatmapPoints, maxValue } = prepareHeatmapData();

    // Configuration Highcharts pour la Heatmap
    const heatmapChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'heatmap',
            height: 350,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.timing.charts.heatmapTitle'),
        },
        subtitle: {
            ...baseOptions.subtitle,
            text: selectedMetric === 'orders'
                ? t('admin.dashboard.analytics.timing.charts.heatmapSubtitle.orders')
                : selectedMetric === 'revenue'
                    ? t('admin.dashboard.analytics.timing.charts.heatmapSubtitle.revenue')
                    : t('admin.dashboard.analytics.timing.charts.heatmapSubtitle.avgCart'),
        },
        xAxis: {
            categories: hourLabels,
            title: { text: t('admin.dashboard.analytics.timing.charts.hour'), style: { color: theme.textColor } },
            labels: {
                style: { color: theme.textColor, fontSize: '10px' },
                step: 2, // Afficher une heure sur deux pour éviter la surcharge
            },
            tickLength: 0,
        },
        yAxis: {
            categories: dayLabels,
            title: { text: '', style: { color: theme.textColor } },
            labels: { style: { color: theme.textColor, fontSize: '11px' } },
            reversed: true,
        },
        colorAxis: {
            min: 0,
            max: maxValue || 1,
            minColor: theme.backgroundColor,
            maxColor: theme.primaryColor,
            labels: {
                style: { color: theme.textColor },
            },
        },
        legend: {
            align: 'right',
            layout: 'vertical',
            verticalAlign: 'middle',
            symbolHeight: 200,
            itemStyle: { color: theme.textColor },
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function (this: any) {
                const day = fullDayLabels[this.point.y];
                const hour = hourLabels[this.point.x];
                const nextHour = hourLabels[(this.point.x + 1) % 24];

                const slotData = heatmapData[this.point.y]?.[this.point.x];
                const orders = slotData?.orders || 0;
                const revenue = slotData?.revenue || 0;
                const avgCart = slotData?.avgCart || 0;

                return `<b>${day} ${hour}-${nextHour}</b><br/>
                        ${t('admin.dashboard.analytics.timing.metrics.orders')}: ${orders}<br/>
                        ${t('admin.dashboard.analytics.timing.metrics.revenue')}: ${formatCurrency(revenue)}<br/>
                        ${t('admin.dashboard.analytics.timing.metrics.avgCart')}: ${formatCurrency(avgCart)}`;
            },
        },
        plotOptions: {
            heatmap: {
                borderWidth: 1,
                borderColor: theme.gridColor,
                dataLabels: {
                    enabled: false,
                },
            },
        },
        series: [{
            name: selectedMetric === 'orders'
                ? t('admin.dashboard.analytics.timing.metrics.orders')
                : selectedMetric === 'revenue'
                    ? t('admin.dashboard.analytics.timing.metrics.revenue')
                    : t('admin.dashboard.analytics.timing.metrics.avgCart'),
            type: 'heatmap',
            data: heatmapPoints,
        }],
        credits: { enabled: false },
    };

    // Bar Chart - Top créneaux
    const topSlotsChartOptions: Highcharts.Options = {
        ...baseOptions,
        chart: {
            ...baseOptions.chart,
            type: 'bar',
            height: 250,
        },
        title: {
            ...baseOptions.title,
            text: t('admin.dashboard.analytics.timing.charts.topSlotsTitle'),
        },
        xAxis: {
            ...baseOptions.xAxis,
            categories: topSlots.map(s => `${s.dayLabel} ${s.hourLabel}`),
            labels: {
                style: { color: theme.textColor, fontSize: '11px' },
            },
        },
        yAxis: {
            ...baseOptions.yAxis,
            title: { text: t('admin.dashboard.analytics.timing.metrics.orders'), style: { color: theme.textColor } },
            allowDecimals: false,
        },
        tooltip: {
            ...baseOptions.tooltip,
            formatter: function (this: any) {
                const slot = topSlots[this.point.index];
                return `<b>${slot?.dayLabel} ${slot?.hourLabel}</b><br/>
                        ${t('admin.dashboard.analytics.timing.metrics.orders')}: ${slot?.orders}<br/>
                        ${t('admin.dashboard.analytics.timing.metrics.revenue')}: ${formatCurrency(slot?.revenue || 0)}`;
            },
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    style: { color: theme.textColor, fontSize: '10px' },
                },
            },
        },
        legend: { enabled: false },
        series: [{
            name: t('admin.dashboard.analytics.timing.metrics.orders'),
            type: 'bar',
            data: topSlots.map((s, i) => ({
                y: s.orders,
                color: i === 0 ? theme.successColor : theme.primaryColor,
            })),
        }],
    };

    return (
        <section className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-primary-10 rounded-lg">
                    <Clock className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{t('admin.dashboard.analytics.timing.title')}</h2>
                <Tooltip
                    title={t('admin.dashboard.analytics.timing.tooltip')}
                    arrow
                >
                    <AlertCircle className="h-4 w-4 text-foreground-secondary cursor-help" />
                </Tooltip>
            </div>

            {/* KPI Cards */}
            <KPICardGrid columns={3}>
                <KPICard
                    title={t('admin.dashboard.analytics.timing.kpis.ordersAnalyzed')}
                    value={data?.totalOrdersAnalyzed || 0}
                    format="number"
                    icon={<TrendingUp className="h-5 w-5 text-primary" />}
                    color="primary"
                    tooltip={t('admin.dashboard.analytics.timing.kpis.ordersAnalyzedTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.timing.kpis.avgOrdersPerSlot')}
                    value={(data?.avgOrdersPerSlot || 0).toFixed(1)}
                    icon={<Clock className="h-5 w-5 text-info" />}
                    color="info"
                    tooltip={t('admin.dashboard.analytics.timing.kpis.avgOrdersPerSlotTooltip')}
                />
                <KPICard
                    title={t('admin.dashboard.analytics.timing.kpis.avgRevenuePerSlot')}
                    value={data?.avgRevenuePerSlot || 0}
                    format="currency"
                    icon={<DollarSign className="h-5 w-5 text-success" />}
                    color="success"
                    tooltip={t('admin.dashboard.analytics.timing.kpis.avgRevenuePerSlotTooltip')}
                />
            </KPICardGrid>

            {/* Best Slot Card */}
            {bestSlot && (
                <div className="bg-gradient-to-r from-primary-10 to-success-10 border border-primary rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-3 bg-primary-10 rounded-xl shadow-sm border border-primary-20">
                            <Zap className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-foreground">{t('admin.dashboard.analytics.timing.bestSlot.title')}</h3>
                            <p className="text-sm text-foreground-secondary">
                                {t('admin.dashboard.analytics.timing.bestSlot.basedOn', { count: data?.totalOrdersAnalyzed || 0 })}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2 mt-4">
                        <span className="text-3xl font-bold text-primary">{bestSlot.dayLabel}</span>
                        <span className="text-2xl font-semibold text-foreground">{bestSlot.hourLabel}</span>
                    </div>
                    <div className="flex gap-6 mt-3">
                        <div>
                            <span className="text-sm text-foreground-secondary">{t('admin.dashboard.analytics.timing.bestSlot.orders')}: </span>
                            <span className="font-semibold text-foreground">{bestSlot.orders}</span>
                        </div>
                        <div>
                            <span className="text-sm text-foreground-secondary">{t('admin.dashboard.analytics.timing.bestSlot.revenue')}: </span>
                            <span className="font-semibold text-foreground">{formatCurrency(bestSlot.revenue)}</span>
                        </div>
                    </div>
                    {!bestSlot.isReliable && (
                        <p className="text-xs text-warning mt-2 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {t('admin.dashboard.analytics.timing.bestSlot.unreliable')}
                        </p>
                    )}
                </div>
            )}

            {/* Metric Selector */}
            <div className="flex gap-2">
                <button
                    onClick={() => setSelectedMetric('orders')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedMetric === 'orders'
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border text-foreground-secondary hover:bg-background-tertiary'
                        }`}
                >
                    {t('admin.dashboard.analytics.timing.metrics.orders')}
                </button>
                <button
                    onClick={() => setSelectedMetric('revenue')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedMetric === 'revenue'
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border text-foreground-secondary hover:bg-background-tertiary'
                        }`}
                >
                    {t('admin.dashboard.analytics.timing.metrics.revenue')}
                </button>
                <button
                    onClick={() => setSelectedMetric('avgCart')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedMetric === 'avgCart'
                        ? 'bg-primary text-white'
                        : 'bg-surface border border-border text-foreground-secondary hover:bg-background-tertiary'
                        }`}
                >
                    {t('admin.dashboard.analytics.timing.metrics.avgCart')}
                </button>
            </div>

            {/* Heatmap */}
            <div className="bg-surface border border-border rounded-xl p-4">
                <HighchartsReact highcharts={Highcharts} options={heatmapChartOptions} />
            </div>

            {/* Top Slots Chart */}
            {topSlots.length > 0 && (
                <div className="bg-surface border border-border rounded-xl p-4">
                    <HighchartsReact highcharts={Highcharts} options={topSlotsChartOptions} />
                </div>
            )}

            {/* Explanation */}
            <div className="bg-background-tertiary rounded-lg p-4 text-sm text-foreground-secondary">
                <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                        <Lightbulb className="h-5 w-5 text-primary" />
                    </div>
                    <span dangerouslySetInnerHTML={{ __html: t('admin.dashboard.analytics.timing.charts.readChart') }} />
                </div>
            </div>
        </section>
    );
};
